import { useId } from 'react'

/**
 * The DDWS mark: one cube, opened along its edges, with a light at its core.
 *
 * The cube is the service, the same object the landing page follows around
 * the world. Its three faces stand a little apart, and the light at the
 * centre shines out between them in three directions: one service, reaching
 * out. Opened up like this, the faces are also what "distributed" looks like
 * at the size of an icon.
 *
 * Drawn inline rather than loaded as an image, so it is sharp at every size
 * and costs no request. The geometry is the brand kit's (the 64-unit drawing
 * the favicon and the logo files are made from), so the mark is the same
 * drawing everywhere it appears. Gradient ids come from useId: a page can show
 * the mark more than once, and an id shared with a hidden copy would leave
 * the visible one without its fill.
 *
 * Decorative wherever it is used: the brand name always sits next to it as
 * text, so it is hidden from assistive technology rather than named twice.
 * Its colours are set in globals.css (`.logo-mark`), with the other raw
 * colours, not here.
 */
export function LogoMark({ className = '' }: { className?: string }) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  return (
    <svg className={`logo-mark ${className}`.trim()} viewBox="8.07 4.6 47.86 53.6" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={`${id}t`} x1="0" y1="0" x2="1" y2="1">
          <stop className="lm-top-a" offset="0" />
          <stop className="lm-top-b" offset="1" />
        </linearGradient>
        <linearGradient id={`${id}l`} x1="0" y1="0" x2=".35" y2="1">
          <stop className="lm-left-a" offset="0" />
          <stop className="lm-left-b" offset="1" />
        </linearGradient>
        <linearGradient id={`${id}r`} x1="0" y1="0" x2=".35" y2="1">
          <stop className="lm-right-a" offset="0" />
          <stop className="lm-right-b" offset="1" />
        </linearGradient>
        <radialGradient id={`${id}g`}>
          <stop className="lm-glow-a" offset="0" />
          <stop className="lm-glow-b" offset=".3" />
          <stop className="lm-glow-c" offset="1" />
        </radialGradient>
      </defs>
      <circle className="logo-mark-glow" cx="32" cy="32" r="10.11" fill={`url(#${id}g)`} />
      <path d="M12.86 18.55Q11.65 17.85 12.86 17.15L30.79 6.8Q32 6.1 33.21 6.8L51.14 17.15Q52.35 17.85 51.14 18.55L33.21 28.9Q32 29.6 30.79 28.9Z" fill={`url(#${id}t)`} />
      <path d="M9.57 22.85Q9.57 21.45 10.78 22.15L28.71 32.5Q29.92 33.2 29.92 34.6L29.92 55.3Q29.92 56.7 28.71 56L10.78 45.65Q9.57 44.95 9.57 43.55Z" fill={`url(#${id}l)`} />
      <path d="M34.08 34.6Q34.08 33.2 35.29 32.5L53.22 22.15Q54.43 21.45 54.43 22.85L54.43 43.55Q54.43 44.95 53.22 45.65L35.29 56Q34.08 56.7 34.08 55.3Z" fill={`url(#${id}r)`} />
      <circle className="lm-core" cx="32" cy="32" r="2.35" />
    </svg>
  )
}
